import React, { useCallback, useId } from "react"

import type { AgentRecord } from "@/types"
import { Input, Label, toast } from "@coval/ui"
import { Copy } from "lucide-react"

import type { FormData } from "@/types/FormData"
import { cn } from "@/utils/cn"

import { AgentPreviewWrapper } from "./AgentPreviewWrapper"
import type { AgentTestConnectionConfig } from "./AgentTestConnectionButton"

interface AgentFormProps {
  formData: FormData
  onFormDataChange: (data: FormData) => void
  currentAgent?: AgentRecord
  testConnection?: AgentTestConnectionConfig | null
  showId?: boolean
}

export const AgentForm: React.FC<AgentFormProps> = ({
  formData,
  onFormDataChange,
  currentAgent,
  testConnection,
  showId = true,
}) => {
  const nameInputId = useId()

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFormDataChange({ ...formData, display_name: e.target.value })
    },
    [formData, onFormDataChange],
  )

  const handleCopyId = useCallback(async () => {
    if (!currentAgent?.id) {
      return
    }

    try {
      await navigator.clipboard.writeText(currentAgent.id)
      toast.success({ title: "ID copied to clipboard" })
    } catch {
      toast.danger({ title: "Failed to copy ID" })
    }
  }, [currentAgent?.id])

  return (
    <div className="space-y-8 w-full">
      <div
        className={cn(
          "grid grid-cols-1 gap-4",
          currentAgent && "md:grid-cols-2",
        )}
      >
        <div className="space-y-2">
          <Label htmlFor={nameInputId}>
            Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id={nameInputId}
            value={formData.display_name}
            onChange={handleNameChange}
            name="display_name"
            placeholder="Enter the name of the agent"
            autoComplete="off"
            required
            data-testid="agent-name-input"
          />
        </div>

        {currentAgent && showId && (
          <div className="space-y-2">
            <Label>ID</Label>
            <Input
              value={currentAgent.id}
              readOnly
              endAdornment={
                <button
                  type="button"
                  onClick={() => {
                    void handleCopyId()
                  }}
                  aria-label="Copy ID"
                >
                  <Copy className="w-4 h-4" />
                </button>
              }
            />
          </div>
        )}
      </div>

      <AgentPreviewWrapper
        formData={formData}
        onFormDataChange={onFormDataChange}
        testConnection={testConnection}
      />
    </div>
  )
}
